import { useCallback, useEffect, useState } from "react";
import {
  FlaskConical, Loader2, Play, GitBranch, ShieldCheck, AlertTriangle, CheckCircle2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS_CLS = {
  DRAFT: "bg-slate-100 text-slate-600",
  TESTING: "bg-amber-100 text-amber-800",
  ACTIVE: "bg-green-100 text-green-800",
  DEPRECATED: "bg-orange-100 text-orange-800",
  ARCHIVED: "bg-slate-200 text-slate-500",
};

export default function AdminMetodologie() {
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/score-methodologies");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setData({ score_methodologies: [], data_quality_methodologies: [] });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/admin/score-methodologies/${id}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const transition = async (id, status) => {
    setBusy(true);
    try {
      await api.post(`/admin/score-methodologies/${id}/transition`, { status });
      toast.success(`Metodologia → ${status}`);
      load();
      if (detail?.methodology?.id === id) openDetail(id);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? `Validazione fallita: ${(d.errors || []).join("; ")}` : formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const newVersion = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/score-methodologies/${id}/new-version`);
      toast.success(`Nuova versione ${data.version} creata come bozza`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const list = data?.score_methodologies || [];
  const dqList = data?.data_quality_methodologies || [];
  const validation = detail?.validation;

  return (
    <div className="space-y-6" data-testid="admin-metodologie-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">Metodologie di score</h1>
        <p className="text-sm text-slate-600 mt-1">
          Versioni, pesi, validazione e pubblicazione. Una metodologia ACTIVE non è modificabile: si crea una nuova versione.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-slate-200" data-testid="methodologies-list">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700">ESG PMI Score — versioni</CardTitle>
          </CardHeader>
          <CardContent>
            {data === null ? <p className="text-sm text-slate-500">Caricamento…</p> : (
              <ul className="divide-y divide-slate-100">
                {list.map((m) => (
                  <li key={m.id} className="py-3 flex flex-wrap items-center gap-3" data-testid={`methodology-row-${m.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{m.name} {m.version}</p>
                      <p className="text-xs text-slate-500">
                        {m.metrics_count} metriche · pesi E/S/G {m.dimension_weights?.E}/{m.dimension_weights?.S}/{m.dimension_weights?.G}%
                      </p>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded ${STATUS_CLS[m.status]}`}
                      data-testid={`methodology-status-${m.id}`}>{m.status}</span>
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => openDetail(m.id)}
                        className="text-xs font-semibold text-green-800 hover:bg-green-50 rounded px-2 py-1.5"
                        data-testid={`methodology-open-${m.id}`}>
                        Dettagli
                      </button>
                      {["DRAFT", "TESTING"].includes(m.status) && (
                        <button type="button" disabled={busy}
                          onClick={() => transition(m.id, m.status === "DRAFT" ? "TESTING" : "ACTIVE")}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-green-700 hover:bg-green-800 rounded px-2 py-1.5"
                          data-testid={`methodology-publish-${m.id}`}>
                          <Play className="w-3 h-3" aria-hidden="true" />
                          {m.status === "DRAFT" ? "Passa a TESTING" : "Attiva"}
                        </button>
                      )}
                      {m.status === "ACTIVE" && (
                        <button type="button" disabled={busy} onClick={() => newVersion(m.id)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 border border-slate-300 hover:bg-slate-100 rounded px-2 py-1.5"
                          data-testid={`methodology-newversion-${m.id}`}>
                          <GitBranch className="w-3 h-3" aria-hidden="true" /> Nuova versione
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card className="border-slate-200" data-testid="dq-methodologies-list">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-700">Data Quality — versioni</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="divide-y divide-slate-100">
                {dqList.map((m) => (
                  <li key={m.id} className="py-2 text-sm" data-testid={`dq-methodology-${m.id}`}>
                    <span className="font-semibold text-slate-900">{m.name} {m.version}</span>
                    <span className={`ml-2 text-xs font-semibold px-2 py-0.5 rounded ${STATUS_CLS[m.status]}`}>{m.status}</span>
                    <p className="text-xs text-slate-500 mt-0.5">
                      pesi: completezza {m.weights?.completeness} · evidenze {m.weights?.evidence} · verifica {m.weights?.verification} · coerenza {m.weights?.consistency} · tracciabilità {m.weights?.traceability} · aggiornamento {m.weights?.freshness}
                    </p>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {detail && (
            <Card className="border-slate-200" data-testid="methodology-detail">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <FlaskConical className="w-4 h-4" aria-hidden="true" />
                  {detail.methodology.name} {detail.methodology.version}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {validation && (
                  <div className={`rounded-lg border p-3 text-xs ${validation.valid ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}
                    data-testid="methodology-validation">
                    <p className="font-semibold flex items-center gap-1">
                      {validation.valid
                        ? <><CheckCircle2 className="w-3.5 h-3.5 text-green-700" aria-hidden="true" /> Validazione superata</>
                        : <><AlertTriangle className="w-3.5 h-3.5 text-red-700" aria-hidden="true" /> Validazione fallita</>}
                    </p>
                    {validation.errors?.map((e, i) => <p key={i} className="text-red-700 mt-1">• {e}</p>)}
                    {validation.warnings?.map((w, i) => <p key={i} className="text-amber-700 mt-1">• {w}</p>)}
                  </div>
                )}
                <div>
                  <p className="text-xs font-semibold text-slate-600 mb-1">Metriche ({detail.metrics.length})</p>
                  <ul className="text-xs text-slate-600 space-y-1 max-h-72 overflow-y-auto">
                    {detail.metrics.map((m) => (
                      <li key={m.code} className="flex flex-wrap gap-x-2" data-testid={`metric-row-${m.code}`}>
                        <span className="font-medium text-slate-800">{m.label}</span>
                        <span className="text-slate-400">{m.dimension} · peso {m.weight}%</span>
                        {m.critical_flag && <span className="text-red-600 font-semibold">critica</span>}
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="text-xs text-slate-500 flex items-start gap-1">
                  <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
                  Soglie: copertura minima {detail.methodology.minimum_coverage?.overall}% · DQ minima per approvazione {detail.methodology.approval_rules?.min_data_quality}
                </p>
              </CardContent>
            </Card>
          )}
          {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-400" aria-hidden="true" />}
        </div>
      </div>
    </div>
  );
}
